// The Adherence Insight pane — the product's thesis, rendered on YOUR own
// corpus. Adherence (not intelligence) is the hard, model-agnostic problem, and
// divergence tracks VOLATILE CONTEXT. This view reads `GET /ingest/insight`
// (owner-scoped) and shows where your sessions break as context grows, plus the
// context size at which the harness gives up and compacts.
//
// At rest with no corpus it is not an empty screen: it tells you exactly how to
// populate it (the consent-gated `waytide-runner scan`). Calm is designed.
//
// The data viz carries ZERO signal
// hue. Amber means "an agent needs you" and nothing else competes — so the
// divergence bars are a neutral data ink (magnitude reads from length + the %
// label) and the KPI cards drop their tone dots entirely. Stat values stay
// text-primary (AA); counts are pluralized; the chart is a labelled list; the
// corpus-wide rate is disambiguated from the per-bucket gradient.

import { useEffect, useState } from "react";
import { getInsight } from "../../api/client";
import type { InsightResponse } from "@waytide/core";
import "./insight.css";

/** Compact token count, honest about magnitude: 324647 → "324k", 1499 → "1.5k",
 * 880 → "880" (never a false 'k'). */
function fmtTokens(n: number): string {
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

/** "1 session" / "2 sessions" — a count must agree with its noun. */
function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function AdherenceInsight() {
  const [data, setData] = useState<InsightResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    getInsight()
      .then((d) => {
        if (!alive) return;
        setData(d);
        setLoaded(true);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="wt-dash-pane wt-insight">
      <header className="wt-dash-pane__head">
        <h1 className="wt-dash-pane__title">Adherence</h1>
        <p className="wt-dash-pane__sub">
          Sessions stall and error more as the context window fills — not because
          of the model. Here's where yours break.
        </p>
      </header>

      {!loaded && <p className="wt-dash-empty">Reading your corpus…</p>}

      {loaded && error && (
        <p className="wt-dash-empty">Couldn't reach your corpus — {error}</p>
      )}

      {loaded && !error && data && data.session_count === 0 && (
        <div className="wt-insight-empty">
          <p>No sessions ingested yet.</p>
          <p className="wt-insight-empty__how">
            On a machine with your agent history, run{" "}
            <code>waytide-runner scan</code> to build your corpus from it. You
            choose what's shared. Then this fills in.
          </p>
        </div>
      )}

      {loaded && !error && data && data.session_count > 0 && (
        <InsightBody data={data} />
      )}
    </div>
  );
}

function InsightBody({ data }: { data: InsightResponse }) {
  const { divergence, compaction, peak_context, buckets, models } = data;
  const worst = buckets.reduce(
    (m, b) => Math.max(m, b.pct_with_divergence),
    0,
  );
  const hasCompaction = compaction.count > 0;

  return (
    <div className="wt-insight-body">
      {/* Headline stat cards — the framing facts. */}
      <div className="wt-insight-cards">
        <Stat
          label="Sessions"
          value={`${data.session_count}`}
          sub={`median peak ${fmtTokens(peak_context.median)}, up to ${fmtTokens(peak_context.max)}`}
        />
        <Stat
          label="Divergence rate"
          value={`${divergence.rate_pct}%`}
          sub={`corpus-wide — up to ${worst}% at high context`}
        />
        <Stat
          label="Context at divergence"
          value={fmtTokens(divergence.context_at_divergence_median)}
          sub={`median — a typical session peaks at just ${fmtTokens(peak_context.median)}`}
        />
        <Stat
          label="Compaction threshold"
          value={hasCompaction ? fmtTokens(compaction.context_at_compaction_median) : "—"}
          sub={
            hasCompaction
              ? `the size the harness pruned at · ${plural(compaction.count, "event")} in ${plural(compaction.sessions, "session")}`
              : "no compaction seen yet"
          }
        />
      </div>

      {/* The headline: divergence rate by peak-context bucket. */}
      <section className="wt-insight-section wt-insight-section--head">
        <h2 className="wt-insight-section__title">
          Divergence rate by context size
        </h2>
        <p className="wt-insight-section__note">
          Your sessions break at high context, not low — same models, the
          difference is how full the window is.
        </p>
        <ul className="wt-insight-buckets">
          {buckets.map((b) => (
            <li className="wt-insight-bucket" key={b.label}>
              <span className="wt-insight-bucket__label">{b.label}</span>
              <span className="wt-insight-bucket__bar-wrap" aria-hidden="true">
                {b.pct_with_divergence > 0 && (
                  <span
                    className="wt-insight-bucket__bar"
                    style={{ width: `${b.pct_with_divergence}%` }}
                  />
                )}
              </span>
              <span className="wt-insight-bucket__pct">
                {b.pct_with_divergence}%
              </span>
              <span className="wt-insight-bucket__meta">
                {plural(b.sessions, "session")} ·{" "}
                {b.tool_err_per_session.toFixed(1)} tool-err/session
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Detail: signal + model breakdowns. */}
      <div className="wt-insight-detail">
        <section className="wt-insight-section">
          <h2 className="wt-insight-section__title">Adherence signals</h2>
          <dl className="wt-insight-defs">
            <Def k="Tool errors" v={`${divergence.tool_errors}`} />
            <Def k="API errors" v={`${divergence.api_errors}`} />
            <Def k="Stop-short" v={`${divergence.stop_short}`} />
            <Def
              k="Compactions"
              v={
                hasCompaction
                  ? `${compaction.count} in ${plural(compaction.sessions, "session")}`
                  : "0"
              }
            />
          </dl>
        </section>

        <section className="wt-insight-section">
          <h2 className="wt-insight-section__title">Models</h2>
          <dl className="wt-insight-defs">
            {models.map((m) => (
              <Def key={m.model} k={m.model} v={`${m.count}`} />
            ))}
          </dl>
        </section>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="wt-insight-card">
      <span className="wt-insight-card__label">{label}</span>
      <span className="wt-insight-card__value">{value}</span>
      <span className="wt-insight-card__sub">{sub}</span>
    </div>
  );
}

function Def({ k, v }: { k: string; v: string }) {
  return (
    <div className="wt-insight-def">
      <dt>{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}
